import React from 'react';
import { X, ArrowUpRight, CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Z_INDEX, getZIndexClass } from '../../utils/zIndexUtils';

interface UpgradeInfo {
  subscriptionId: string;
  currentPlan: 'standard' | 'premium';
  targetPlan: 'premium';
  currentPrice: number;
  newPrice: number;
  currency: 'thb' | 'usd';
  isRecurring: boolean;
  serviceType: 'flower' | 'fruit' | 'cat_grass';
  // For one-time subscriptions
  remainingDeliveries?: number;
  upgradeAmount?: number;
  // For recurring subscriptions
  prorationAmount?: number;
  nextBillingDate?: string;
}

interface UpgradeConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  upgradeInfo: UpgradeInfo | null;
  loading?: boolean;
}

const UpgradeConfirmationModal: React.FC<UpgradeConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  upgradeInfo,
  loading = false
}) => {
  const { t } = useTranslation();

  if (!isOpen || !upgradeInfo) return null;

  const formatCurrency = (amount: number, currency: string) => {
    const symbol = currency === 'usd' ? '$' : '฿';
    return `${symbol}${amount.toLocaleString()}`;
  };

  const getPlanDisplayName = (plan: string) => {
    return plan === 'standard' ? t('subscription.plans.standard') : t('subscription.plans.premium');
  };

  const getServiceDisplayName = (serviceType: string) => {
    switch (serviceType) {
      case 'flower':
        return t('subscription.serviceTypes.flower');
      case 'fruit':
        return t('subscription.serviceTypes.fruit');
      case 'cat_grass':
        return t('subscription.serviceTypes.catGrass');
      default:
        return serviceType;
    }
  };

  const renderRecurringUpgradeDetails = () => (
    <div className="space-y-4">
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="flex items-start">
          <CheckCircle className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-blue-900">
              {t('upgradeConfirmation.recurring.title')}
            </h4>
            <p className="text-sm text-blue-700 mt-1">
              {t('upgradeConfirmation.recurring.description')}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">{t('upgradeConfirmation.currentPlan')}</span>
          <span className="font-medium">{getPlanDisplayName(upgradeInfo.currentPlan)}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-600">{t('upgradeConfirmation.currentMonthlyPrice')}</span>
          <span className="font-medium">{formatCurrency(upgradeInfo.currentPrice, upgradeInfo.currency)}</span>
        </div>
        <div className="border-t pt-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">{t('upgradeConfirmation.newPlan')}</span>
            <span className="font-medium text-green-600">{getPlanDisplayName(upgradeInfo.targetPlan)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-600">{t('upgradeConfirmation.newMonthlyPrice')}</span>
            <span className="font-medium">{formatCurrency(upgradeInfo.newPrice, upgradeInfo.currency)}</span>
          </div>
        </div>
      </div>

      {upgradeInfo.prorationAmount !== undefined && upgradeInfo.prorationAmount > 0 && (
        <div className="bg-yellow-50 p-4 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-yellow-900">
                {t('upgradeConfirmation.recurring.prorationTitle')}
              </h4>
              <p className="text-sm text-yellow-700 mt-1">
                {t('upgradeConfirmation.recurring.prorationDescription', {
                  amount: formatCurrency(upgradeInfo.prorationAmount, upgradeInfo.currency)
                })}
              </p>
              {upgradeInfo.nextBillingDate && (
                <p className="text-sm text-yellow-700 mt-2">
                  {t('upgradeConfirmation.recurring.nextBilling', {
                    date: new Date(upgradeInfo.nextBillingDate).toLocaleDateString()
                  })}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderOneTimeUpgradeDetails = () => (
    <div className="space-y-4">
      <div className="bg-green-50 p-4 rounded-lg">
        <div className="flex items-start">
          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-green-900">
              {t('upgradeConfirmation.oneTime.title')}
            </h4>
            <p className="text-sm text-green-700 mt-1">
              {t('upgradeConfirmation.oneTime.description')}
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">{t('upgradeConfirmation.currentPlan')}</span>
          <span className="font-medium">{getPlanDisplayName(upgradeInfo.currentPlan)}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-gray-600">{t('upgradeConfirmation.remainingDeliveries')}</span>
          <span className="font-medium">{upgradeInfo.remainingDeliveries || 0}</span>
        </div>
        <div className="border-t pt-3">
          <div className="flex justify-between items-center">
            <span className="text-gray-600">{t('upgradeConfirmation.newPlan')}</span>
            <span className="font-medium text-green-600">{getPlanDisplayName(upgradeInfo.targetPlan)}</span>
          </div>
          {upgradeInfo.upgradeAmount !== undefined && (
            <div className="flex justify-between items-center mt-2">
              <span className="text-gray-600">{t('upgradeConfirmation.oneTime.upgradeAmount')}</span>
              <span className="font-semibold text-lg text-green-600">
                {formatCurrency(upgradeInfo.upgradeAmount, upgradeInfo.currency)}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="bg-yellow-50 p-4 rounded-lg">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
          <div>
            <h4 className="font-medium text-yellow-900">
              {t('upgradeConfirmation.oneTime.paymentRequired')}
            </h4>
            <p className="text-sm text-yellow-700 mt-1">
              {t('upgradeConfirmation.oneTime.paymentDescription')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 ${getZIndexClass(Z_INDEX.FULL_SCREEN_MODAL)}`}>
      <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {t('upgradeConfirmation.title')}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {getServiceDisplayName(upgradeInfo.serviceType)} {t('subscription.subscription')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            disabled={loading}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {upgradeInfo.isRecurring ? renderRecurringUpgradeDetails() : renderOneTimeUpgradeDetails()}
        </div>

        {/* Footer */}
        <div className="flex space-x-3 p-6 border-t bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center"
          >
            {loading ? (
              <div className="w-5 h-5 border-t-2 border-r-2 border-white rounded-full animate-spin mr-2"></div>
            ) : (
              <ArrowUpRight className="w-4 h-4 mr-2" />
            )}
            {loading ? t('common.processing') : t('upgradeConfirmation.confirmUpgrade')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradeConfirmationModal; 